// This utility enhances the existing locationUtils with better geographic mapping
import { LOCATION_DATABASE, COUNTRY_FLAGS, TIMEZONE_INFO, getCountryFlag, getTimezoneInfo } from './locationUtils'; 

/**
 * Enhances a location object with precise geographic coordinates and visualization data
 * @param {Object} location - The original location object from the API
 * @param {number} index - Index in the locations array
 * @param {number} total - Total number of locations
 * @returns {Object} - Enhanced location object with real geographic coordinates
 */
export const enhanceLocationWithGeoData = (location, index, total) => {
  // Parse the location name to find matching geographical coordinates

 
// Add this function to your code to normalize location names for better matching
const normalizeLocationName = (name) => {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')        // Replace multiple spaces with single space
    .replace(/[,\-_\.]/g, ' ')   // Replace common separators with space
    .replace(/\bcity\b|\boffice\b|\bcenter\b|\bcentre\b/g, ''); // Remove common suffixes
};

// Then in your enhanceLocationWithGeoData function, use:
let locName = normalizeLocationName(location.global_location_name || '');
  // Try to find exact match in the database
  let locationInfo = LOCATION_DATABASE[locName];
  
  // If no direct match, try to identify city/country by tokens in the name
  if (!locationInfo) {
    // Break name into tokens and check each
    const tokens = locName.split(/[\s,\-_]+/);
    for (const token of tokens) {
      if (token.length > 2 && LOCATION_DATABASE[token]) {
        locationInfo = LOCATION_DATABASE[token];
        break;
      }
    }
    
    // If still no match, check if any location name is contained in the string
    if (!locationInfo) {
      for (const [key, value] of Object.entries(LOCATION_DATABASE)) {
        if (locName.includes(key) && key.length > 2) {
          locationInfo = value;
          break;
        }
      }
    }
  }
  
  // If we couldn't find a match, use a placeholder position
  if (!locationInfo) {
    // Generate a random but visually pleasing position on the map
    // These ranges keep markers on land masses more often than not
    const placeholderLat = Math.random() * 70 - 35; // Range: -35 to 35 (avoiding extreme poles)
    const placeholderLng = Math.random() * 340 - 170; // Range: -170 to 170
    
    return {
      id: location.global_id || `loc-${index}`,
      name: location.global_location_name,
      lat: placeholderLat,
      lng: placeholderLng,
      country: 'Unknown Region',
      countryCode: 'GL',
      flag: getCountryFlag('GL'),
      timezone: 'UTC',
      timezoneName: getTimezoneInfo('UTC').timezoneName,
      organizationalCode: location.Organizational_Code,
      gigsId: location.Gigs_id,
      gigsName: location.Gigs_name,
      // Status indicators and mock data
      initiatives: getRandomStatus(),
      incidents: getRandomStatus(),
      details: generateMockDetails(),
      isPlaceholder: true // Flag that this is a placeholder location
    };
  }
  
  // Use actual geo coordinates for the location
  return {
    id: location.global_id || `loc-${index}`,
    name: location.global_location_name,
    lat: locationInfo.lat,
    lng: locationInfo.lng,
    country: locationInfo.country,
    countryCode: locationInfo.countryCode,
    flag: getCountryFlag(locationInfo.countryCode),
    timezone: locationInfo.timezone,
    timezoneName: getTimezoneInfo(locationInfo.timezone).timezoneName,
    organizationalCode: location.Organizational_Code,
    gigsId: location.Gigs_id,
    gigsName: location.Gigs_name,
    // Status indicators and mock data
    initiatives: getRandomStatus(),
    incidents: getRandomStatus(),
    details: generateMockDetails()
  };
};

/**
 * Generate a random status indicator (green, yellow, red)
 * @returns {string} Status color
 */
const getRandomStatus = () => {
  const statuses = ['green', 'yellow', 'red'];
  return statuses[Math.floor(Math.random() * 3)];
};

/**
 * Generate mock details for a location
 * @returns {Object} Mock details with random values
 */
const generateMockDetails = () => {
  return {
    employees: Math.floor(Math.random() * 200) + 50,
    activeIncidents: Math.floor(Math.random() * 10),
    resolvedIncidents: Math.floor(Math.random() * 30) + 10,
    ongoingInitiatives: Math.floor(Math.random() * 10) + 1,
    completedInitiatives: Math.floor(Math.random() * 20) + 5,
    performance: {
      sales: `+${Math.floor(Math.random() * 20) + 5}% MoM`,
      customer: `${Math.floor(Math.random() * 10) + 90}% satisfaction`,
      operations: `${(Math.random() * 5 + 95).toFixed(1)}% uptime`
    }
  };
};

/**
 * Get all cities in a specific country with proper geo data
 * @param {string} countryCode - The country code to filter by
 * @returns {Array} Array of city objects with geo data
 */
export const getGeoMappedCitiesForCountry = (countryCode) => {
  const cities = [];
  
  // Find all locations in our database for this country
  for (const [name, data] of Object.entries(LOCATION_DATABASE)) {
    if (data.countryCode === countryCode && name !== data.country.toLowerCase()) {
      cities.push({
        name: name.charAt(0).toUpperCase() + name.slice(1), // Capitalize first letter
        lat: data.lat,
        lng: data.lng,
        timezone: data.timezone,
        timezoneName: getTimezoneInfo(data.timezone).timezoneName
      });
    }
  }
  
  return cities;
};

/**
 * Check if a string contains a known city or country name
 * @param {string} name - Name to check
 * @returns {boolean} True if the name contains a known location
 */
export const containsKnownLocation = (name) => {
  name = name.toLowerCase().trim();
  
  // Check direct match
  if (LOCATION_DATABASE[name]) return true;
  
  // Check tokens
  const tokens = name.split(/[\s,\-_]+/);
  for (const token of tokens) {
    if (token.length > 2 && LOCATION_DATABASE[token]) return true;
  }
  
  // Check substrings
  for (const key of Object.keys(LOCATION_DATABASE)) {
    if (name.includes(key) && key.length > 2) return true;
  }
  
  return false;
};